
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import {
  SidebarMenuButton,
  SidebarMenuItem as SidebarMenuItemBase,
  SidebarMenuSub,
} from "@/components/ui/sidebar";
import { SidebarMenuSubItem } from "./SidebarMenuSubItem";
import type { MenuItem } from "./menuItems";

interface SidebarMenuItemProps {
  item: MenuItem;
  isCollapsed: boolean;
  showIcons: boolean;
  expandedItems: string[];
  toggleExpanded: (title: string) => void;
  isActive: (href: string) => boolean;
  isParentActive: (item: MenuItem) => boolean;
  level?: number;
}

export function SidebarMenuItem({
  item,
  isCollapsed,
  showIcons,
  expandedItems,
  toggleExpanded,
  isActive,
  isParentActive,
  level = 0
}: SidebarMenuItemProps) {
  if (!item.icon) {
    console.warn(`Missing icon for menu item: ${item.title}`);
    return null;
  }

  const isExpanded = expandedItems.includes(item.title);
  const isItemActive = isParentActive(item);

  if (item.subItems && item.subItems.length > 0) {
    return (
      <SidebarMenuItemBase dir="rtl">
        <SidebarMenuButton
          onClick={() => toggleExpanded(item.title)}
          className={`
            group relative overflow-hidden
            ${isCollapsed ? 'justify-center w-11 h-11 p-0 mx-auto' : 'justify-between px-3 py-2.5'}
            rounded-xl transition-all duration-300
            ${isItemActive 
              ? 'bg-primary/10 text-primary shadow-sm ring-1 ring-primary/20' 
              : 'hover:bg-accent/50 text-foreground/80 hover:text-foreground'
            }
          `}
          tooltip={isCollapsed ? item.title : undefined}
          dir="rtl"
        >
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`} dir="rtl">
            {(showIcons || isCollapsed) && (
              <div className={`
                relative flex items-center justify-center flex-shrink-0 transition-all duration-300
                ${isCollapsed ? 'w-6 h-6' : 'w-9 h-9'}
                ${isItemActive 
                  ? 'bg-gradient-to-br from-primary to-primary/80 shadow-lg shadow-primary/30' 
                  : 'bg-gradient-to-br ' + (item.color || 'from-muted-foreground/70 to-muted-foreground/50') + ' group-hover:shadow-md'
                }
                rounded-lg
              `}>
                <item.icon className={`text-white ${isCollapsed ? 'w-3.5 h-3.5' : 'w-4.5 h-4.5'}`} />
              </div>
            )}
            {!isCollapsed && (
              <span className={`
                font-medium text-sm truncate
                ${isItemActive ? 'text-primary font-semibold' : 'text-foreground/80 group-hover:text-foreground'}
              `}>
                {item.title}
              </span>
            )}
          </div>
          {!isCollapsed && (
            <ChevronLeft 
              className={`
                w-4 h-4 transition-all duration-300 flex-shrink-0
                ${isItemActive ? 'text-primary' : 'text-muted-foreground'}
                ${isExpanded ? '-rotate-90' : ''}
              `} 
            />
          )}
        </SidebarMenuButton>
        {!isCollapsed && isExpanded && (
          <SidebarMenuSub className="sidebar-submenu !mx-0 !px-0 !border-l-0 mt-1 space-y-0.5" dir="rtl">
            {item.subItems.map((subItem) => (
              subItem.subItems ? (
                <SidebarMenuItem
                  key={subItem.title}
                  item={subItem}
                  isCollapsed={isCollapsed}
                  showIcons={showIcons}
                  expandedItems={expandedItems}
                  toggleExpanded={toggleExpanded}
                  isActive={isActive}
                  isParentActive={isParentActive}
                  level={level + 1}
                />
              ) : (
                <SidebarMenuSubItem
                  key={subItem.title}
                  item={subItem}
                  showIcons={showIcons}
                  isActive={isActive}
                  level={level + 1}
                />
              )
            ))}
          </SidebarMenuSub>
        )}
      </SidebarMenuItemBase>
    );
  }

  const isLinkActive = isActive(item.href);

  return (
    <SidebarMenuItemBase dir="rtl">
      <SidebarMenuButton 
        asChild 
        isActive={isLinkActive}
        className={`
          group relative overflow-hidden
          ${isCollapsed ? 'justify-center w-11 h-11 p-0 mx-auto' : 'justify-start px-3 py-2.5'}
          rounded-xl transition-all duration-300
          ${isLinkActive 
            ? 'bg-primary/10 text-primary shadow-md ring-2 ring-primary/30 font-semibold' 
            : 'hover:bg-accent/50 text-foreground/80 hover:text-foreground'
          }
        `}
        tooltip={isCollapsed ? item.title : undefined}
        dir="rtl"
      >
        <Link 
          to={item.href} 
          className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} w-full`} 
          dir="rtl"
          onClick={(e) => {
            // إغلاق القائمة الجانبية على الموبايل عند النقر
            const mobileDrawer = document.querySelector('[role="dialog"]');
            if (mobileDrawer && window.innerWidth < 1024) {
              const closeButton = mobileDrawer.querySelector('button[aria-label="Close"]');
              if (closeButton) {
                (closeButton as HTMLButtonElement).click();
              }
            }
          }}
        >
          {(showIcons || isCollapsed) && (
            <div className={`
              relative flex items-center justify-center flex-shrink-0 transition-all duration-300
              ${isCollapsed ? 'w-6 h-6' : 'w-9 h-9'}
              ${isLinkActive 
                ? 'bg-primary shadow-lg shadow-primary/40 scale-110' 
                : 'bg-gradient-to-br ' + (item.color || 'from-muted-foreground/70 to-muted-foreground/50') + ' group-hover:shadow-md'
              }
              rounded-lg
            `}>
              <item.icon className={`text-white ${isCollapsed ? 'w-3.5 h-3.5' : 'w-4.5 h-4.5'}`} />
            </div>
          )}
          {!isCollapsed && (
            <span className={`
              font-medium text-sm truncate
              ${isLinkActive ? 'text-primary font-bold' : 'text-foreground/80 group-hover:text-foreground'}
            `}>
              {item.title}
            </span>
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItemBase>
  );
}
